import {
  GET_COMMENTS,
  MATERIAL_LIST,
  FEATURE_ATTACHMENTS
} from "services/graphQL/ciq-queries";
import { getGQL } from "utils/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import CIQClient from "services/ciq-client";
import { BASE_URL } from "services/endpoints";
import { useQuerySubscription } from "./common";
import { useCIQQuery } from "./ciq-gql-hooks";

export const useMaterialList = (params: {
  gqlClientForProject: any;
  skipSubscribe?: any;
}): { data: any; error: any; loading: boolean } => {
  const { gqlClientForProject, skipSubscribe = false } = params;

  const { query: qMaterialList, sub: sMaterialList } = getGQL(MATERIAL_LIST);
  const {
    data: materialListData,
    error,
    loading
  } = useQuerySubscription(qMaterialList, sMaterialList, {
    client: gqlClientForProject,
    skip: skipSubscribe
  });

  return {
    data: materialListData?.material_list_func,
    error,
    loading
  };
};

export const useMaterialAttachments = (params: {
  materialId: string;
  gqlClientForProject: any;
}): {
  data:
    | {
        attachments: any[];
        commentsData: {
          comment: Array<any>;
        };
      }
    | undefined;
  error: any;
  refetchCommentData: Function;
} => {
  const { materialId, gqlClientForProject } = params;

  const { data: commentsData, refetch: refetchCommentData } = useCIQQuery<{
    comment: Array<any>;
  }>(GET_COMMENTS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject,
    variables: {
      where: { material_id: { _eq: materialId } }
    }
  });

  // FEATURE_ATTACHMENTS ignore the name, query is same for submittal and material attachments
  const { data, error } = useCIQQuery(FEATURE_ATTACHMENTS, {
    variables: {
      where: {
        material_id: { _eq: materialId }
      }
    },
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const mergedAttachments = useMemo(() => {
    if (data && commentsData) {
      // console.log({ data, commentsData });
      const allCommentAttachments: any = [];
      commentsData.comment.forEach((comment: any) => {
        const attachmentInfo = comment.attachments?.map((attach: any) => {
          return {
            id: attach.id,
            file_name: attach.file_name,
            created_at: comment.created_at,
            created_by_user: comment.created_by_user,
            blob_key: attach.blob_key,
            file_type: attach.file_type
          };
        });
        allCommentAttachments.push(...attachmentInfo);
      });
      return {
        attachments: [...allCommentAttachments, ...data.attachments],
        commentsData
      };
    }
    return undefined;
  }, [commentsData, data]);

  return { data: mergedAttachments, error, refetchCommentData };
};

async function fetchLocations(
  searchTerm: string,
  projectToken: string
): Promise<any> {
  const config = {
    headers: { Authorization: `Bearer ${projectToken}` }
  };

  return CIQClient.get({
    url: `${BASE_URL}/location-autoComplete?queryText=${searchTerm}`,
    config
  });
}

export const useShippingLocation = (params: {
  gqlClientForProject: any;
  projectToken: string;
}) => {
  const { projectToken } = params;

  const [fetching, setFetching] = useState(false);
  const [responseData, setResponseData] = useState<any[]>([]);
  const [searchedString, setSearchedString] = useState("");
  const fetchRef = useRef(0);
  const mountedRef = useRef(true);
  const debouncedFnRef = useRef<ReturnType<typeof debounce>>();

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      debouncedFnRef.current?.cancel();
    };
  }, []);

  const getLocations = useCallback(
    async (searchTerm: string) => {
      setSearchedString(searchTerm);

      if (searchTerm?.trim()) {
        setFetching(true);
        setResponseData([]);
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        const res: {
          success: boolean;
          data: { success: { label: string; placeId: string }[] };
        } = await fetchLocations(searchTerm, projectToken);

        if (mountedRef.current && fetchId === fetchRef.current && res.success) {
          setResponseData(res.data.success);
          setFetching(false);
        }
      } else if (mountedRef.current) {
        setResponseData([]);
        setFetching(false);
      }
    },
    [projectToken]
  );

  const searchLocation = useMemo(() => {
    debouncedFnRef.current = debounce(getLocations, 800);

    return (searchTerm: string) => {
      if (mountedRef.current) {
        return debouncedFnRef.current?.(searchTerm);
      }
      return null;
    };
  }, [getLocations]);

  const selectOptions = useMemo(() => {
    return responseData.map((resultItem: any) => ({
      label: resultItem.label,
      value: resultItem.placeId
    }));
  }, [responseData]);

  const clearOptions = useCallback(() => {
    if (mountedRef.current) {
      setResponseData([]);
      setSearchedString("");
    }
  }, []);

  return {
    searchLocation,
    fetching,
    responseData,
    selectOptions,
    searchedString,
    clearOptions
  };
};
